import { Link } from "react-router-dom";

const PrivacyPage = () => {
  const lastUpdated = "June 2026";

  return (
    <div className="bg-white" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-20">
        <p className="text-sm uppercase tracking-widest text-slate-500 mb-4">Legal</p>
        <h1
          className="text-4xl sm:text-5xl font-bold text-[#0A0A0A] mb-2"
          style={{ fontFamily: "Barlow Condensed, sans-serif" }}
        >
          Privacy Policy
        </h1>
        <p className="text-sm text-slate-500 mb-12">Last updated: {lastUpdated}</p>

        <div className="prose prose-slate max-w-none space-y-8 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-[#1a365d] mb-3">1. Who we are</h2>
            <p>
              Order &amp; Clean Co (ABN <strong>73 677 568 884</strong>), referred to in this policy
              as "we", "us" or "our", operates a residential garage cleanout, organisation and space
              enhancement service across Sydney and surrounding regions in New South Wales,
              Australia. This Privacy Policy explains how we collect, use, store and disclose your
              personal information in accordance with the Australian Privacy Principles under the{" "}
              <em>Privacy Act 1988</em> (Cth).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1a365d] mb-3">2. Information we collect</h2>
            <p>When you use our website, request a quote or make a booking, we may collect:</p>
            <ul className="list-disc ml-6 space-y-2 mt-3">
              <li>Your name, email address, phone number and service address;</li>
              <li>Information you give us about your garage, items to keep or discard, and the service you require;</li>
              <li>Photos or notes you upload to help us provide an accurate quote;</li>
              <li>Payment information processed by our payment provider (we do not store full card details on our servers);</li>
              <li>Technical information such as your IP address, browser type and pages visited.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1a365d] mb-3">3. How we use your information</h2>
            <p>We use your personal information to:</p>
            <ul className="list-disc ml-6 space-y-2 mt-3">
              <li>Provide quotes and deliver the services you book;</li>
              <li>Process your $100 deposit and balance payments;</li>
              <li>Send booking confirmations, 24-hour appointment reminders and post-service follow-ups;</li>
              <li>Suggest relevant space enhancement upgrades (you can opt out at any time);</li>
              <li>Respond to your enquiries and provide customer support;</li>
              <li>Improve our website, services and operations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1a365d] mb-3">4. Third-party service providers</h2>
            <p>
              To run our business we share limited personal information with the following processors,
              each bound by their own privacy obligations:
            </p>
            <ul className="list-disc ml-6 space-y-2 mt-3">
              <li>
                <strong>Stripe Payments Australia Pty Ltd</strong> — processes your card payments. Stripe
                operates under its own privacy policy at stripe.com/au/privacy.
              </li>
              <li>
                <strong>Resend</strong> — delivers our transactional and service emails on our behalf.
              </li>
              <li>
                <strong>MongoDB Atlas</strong> — securely stores your booking and customer records.
              </li>
              <li>
                Partner installers — where you book a space enhancement service we coordinate with
                approved installers and share only the information they need to complete the work.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1a365d] mb-3">5. Marketing emails &amp; unsubscribing</h2>
            <p>
              We send post-service follow-up emails that may include suggestions for relevant space
              enhancements. You can unsubscribe from these marketing emails at any time using the
              link in the email footer, or by replying with "unsubscribe". Transactional emails such
              as booking confirmations and appointment reminders will continue to be sent so we can
              service your booking.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1a365d] mb-3">6. Storage and security</h2>
            <p>
              We take reasonable steps to protect your personal information from misuse, loss,
              unauthorised access, modification or disclosure. Information is stored on encrypted
              database services and access is restricted to authorised personnel. No system is
              perfectly secure, so we encourage you to take care when sharing information online.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1a365d] mb-3">7. Accessing and correcting your information</h2>
            <p>
              You have the right to request access to the personal information we hold about you and
              to ask us to correct it if it is inaccurate. To make a request, email{" "}
              <a href="mailto:hello@orderandcleanco.com.au" className="text-[#1a365d] underline">
                hello@orderandcleanco.com.au
              </a>
              . We may ask you to verify your identity before releasing information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1a365d] mb-3">8. Complaints</h2>
            <p>
              If you believe we have breached your privacy, contact us first at the email above and we
              will respond within 30 days. If you are not satisfied with our response, you can lodge a
              complaint with the Office of the Australian Information Commissioner (OAIC) at oaic.gov.au.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1a365d] mb-3">9. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The "Last updated" date at the top
              of this page will reflect the most recent revision. Continued use of our services after
              changes means you accept the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1a365d] mb-3">10. Contact</h2>
            <p>
              Order &amp; Clean Co<br />
              ABN 73 677 568 884<br />
              Sydney, NSW, Australia<br />
              <a href="mailto:hello@orderandcleanco.com.au" className="text-[#1a365d] underline">
                hello@orderandcleanco.com.au
              </a>
            </p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-200">
          <Link
            to="/"
            className="text-[#1a365d] font-semibold hover:underline"
            data-testid="privacy-back-home"
          >
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
